<template>
  <div class="event-search-filters">
    <h3 class="event-search-filters__heading">
      Filters
    </h3>

    <b-form-group label="Month:"
                  :label-for="fieldId.month">
      <b-select
        :id="fieldId.month"
        v-model="internalMonth"
        aria-label="Months filter"
        :options="options">
      </b-select>
    </b-form-group>

    <b-form-group v-if="enableTypeFilter"
                  label="Event type:"
                  :label-for="fieldId.eventType">
      <b-form-checkbox-group
        :id="fieldId.eventType"
        v-model="internalTypes"
        :options="instanceTypeOptions"
        stacked></b-form-checkbox-group>
    </b-form-group>

    <b-form-group v-if="enableSubThemeFilter"
                  label="Sub theme:"
                  :label-for="fieldId.subTheme">
      <b-select
        :id="fieldId.subTheme"
        v-model="internalSubTheme"
        aria-label="Sub Themes filter"
        :options="subThemeOptions">
      </b-select>
    </b-form-group>

    <b-form-group label="Sort by:"
                  :label-for="fieldId.sortBy">
      <b-select
        :id="fieldId.sortBy"
        v-model="sort"
        aria-label="Sort by filter"
        :options="sortOptions">
      </b-select>
    </b-form-group>
  </div>
</template>

<script>
import axios from "axios";
import { isEqual } from "date-fns";
import { uniqueId } from "lodash";
import { loadInstanceFilters } from "../../Scripts/resources/eventsResource";

export default {
  name: "EventSearchFilters",
  props: {
    selectedMonth: {
      type: Date,
      default: null
    },
    selectedTypes: {
      type: Array,
      default: () => []
    },
    enableTypeFilter: {
      type: Boolean,
      default: false
    },
    enableSubThemeFilter: {
      type: Boolean,
      default: false
    }
  },
  data: () => ( {
    month: null,
    internalMonth: null,
    internalSubTheme: null,
    instanceTypes: [],
    internalTypes: [],
    subThemes: [],
    sort: null,
    dates: null
  } ),
  computed: {
    creationId () {
      return uniqueId( "event-search-filters-" );
    },
    fieldId () {
      return {
        month: `${this.creationId}-month-filter`,
        eventType: `${this.creationId}-event-type`,
        subTheme: `${this.creationId}-sub-theme`,
        sortBy: `${this.creationId}-sort-by`
      };
    },
    sortOptions () {
      const result = [
        {
          text: "Name",
          value: null
        },
        {
          text: "Date (Ascending)",
          value: "date"
        },
        {
          text: "Date (Descending)",
          value: "dateDesc"
        }
      ];

      return result;
    },
    instanceTypeOptions () {
      return this.instanceTypes.map( x => ( {
        text: x.instanceType,
        value: x.eventInstanceTypeId
      } ) );
    },
    subThemeOptions () {
      const result = [
        {
          text: "All sub themes",
          value: null
        },
        ...this.subThemes.map( x => ( {
          text: x.tagDescription || x.tagName,
          value: x.tagName
        } ) )
      ];
      return result;
    },
    options () {
      const result = [
        {
          text: "All months",
          value: null
        }
      ];

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();

      for ( let i = 0; i < 24; i += 1 ) {
        const monthOffset = currentMonth + i;
        const date = new Date( currentYear, monthOffset );

        if ( this.maxDate ) {
          const maxDate = Date.parse( this.maxDate );
          if ( date >= maxDate ) {
            break;
          }
        }

        result.push( {
          text: this.getMonthWord( date ),
          value: date
        } );
      }

      return result;
    },
    maxDate () {
      let result = null;

      if ( this.dates ) {
        const dates = Object.keys( this.dates );

        if ( dates?.length > 0 ) {
          result = dates.reduce( ( acc, cv ) => ( acc > cv ? acc : cv ) );
        }
      }

      return result;
    }
  },
  watch: {
    sort: {
      immediate: true,
      handler ( val ) {
        this.$emit( "select-sort", val );
      }
    },
    internalMonth ( val ) {
      this.$emit( "select-month", val );
    },
    internalTypes ( val ) {
      this.$emit( "select-type", val );
    },
    internalSubTheme ( val ) {
      this.$emit( "select-sub-theme", val );
    },
    selectedTypes: {
      immediate: true,
      deep: true,
      handler ( val ) {
        this.internalTypes = val;
      }
    },
    selectedMonth: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          const currentYear = new Date( val ).getFullYear();
          const currentMonth = new Date( val ).getMonth();
          const date = new Date( currentYear, currentMonth );

          if ( !isEqual( date, this.internalMonth ) ) {
            this.internalMonth = date;
          }
        } else {
          this.internalMonth = null;
        }
      }
    }
  },
  created () {
    loadInstanceFilters().then( response => {
      this.instanceTypes = response.data?.instanceTypes || [];
      this.subThemes = response.data?.subThemes || [];
    } );

    this.fetchData();
  },
  methods: {
    fetchData () {
      axios
        .post( "/umbraco/Surface/eventSearch/Count", null, {
          params: {}
        } )
        .then( ( { data } ) => {
          this.dates = data.dates;
        } );
    },
    getMonthWord ( date ) {
      return date.toLocaleString( "en-GB", {
        month: "long",
        year: "numeric"
      } );
    }
  }
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.event-search-filters {
  padding: 15px;

  &__heading {
    font-size: 1rem;
    font-weight: 700;
    color: @color-accent-6;
  }

  /deep/ .col-form-label {
    padding-bottom: 3px !important;
  }

  .custom-select {
    border: 2px solid #78b7bc;
    border-radius: 0;
  }

  /deep/ legend,
  /deep/ label {
    color: #016C7F;
    font-size: 0.875rem;
    font-weight: 600;
  }
}
</style>
